/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, InputGroup } from 'react-bootstrap';
import HVASpinner from 'components/base/HVASpinner';
import { useAppContext } from 'providers/AppProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPen,
  faUndo,
  faInfoCircle,
  faC,
  faExclamationCircle,
  faProjectDiagram
} from '@fortawesome/free-solid-svg-icons';
import {
  UilEar,
  UilBrain,
  UilCommentDots,
  UilFileLandscapeAlt
} from '@iconscout/react-unicons';
import {
  getAgentData,
  getAgentTools,
  getAvailableTools,
  updateAgentFirstMessage,
  updateAgentInboundFirstMessage,
  updateAgentPrompt,
  updateAgentInboundPrompt,
  updateAgentCallResultPrompt,
  getAgentValidations,
  assignToolToAgent,
  removeToolFromAgent,
  updateToolValidations,
  AIAdapterConfig,
  getAllSttConfigs,
  getAllLlmConfigs,
  getAllTtsConfigs,
  getAllSummaryConfigs,
  updateAgent
} from 'services/hvaagent.service';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import VariableAutocomplete from './HVAFineTuningPlaygroundVariableAutocomplete';
import MermaidDiagramModal from './MermaidDiagramModal';
import HVAToast from 'components/common/HVAToast';
import LexicalPromptEditor from './LexicalPromptEditor';
import './LexicalPromptEditor.css';

// Importaciones de banderas
import spanish from 'assets/img/flags/spainl.png';
import english from 'assets/img/flags/englandl.png';
import american from 'assets/img/flags/usal.png';
import greek from 'assets/img/flags/greecel.png';
import german from 'assets/img/flags/germanyl.png';
import italian from 'assets/img/flags/italyl.png';
import french from 'assets/img/flags/francel.png';
import brasilian from 'assets/img/flags/brazill.png';
import chilean from 'assets/img/flags/chilel.png';
import mexican from 'assets/img/flags/mexicol.png';

// Grupos de herramientas de ConversationalEngine
const TOOL_GROUPS = {
  REQUEST_INFO: 'requestInfo',
  REQUEST_VALIDATION: 'requestValidation',
  REQUEST_ACTION: 'requestAction'
};

// Define tipos para prompts
enum PromptType {
  FIRST_MESSAGE = 'firstMessage',
  INBOUND_FIRST_MESSAGE = 'inboundFirstMessage',
  OUTBOUND = 'outbound',
  INBOUND = 'inbound',
  CALL_RESULT = 'callResult'
}

// Agregar lista de idiomas disponibles
const AVAILABLE_LANGUAGES = [
  { code: 'es-ES', name: 'Spanish (Spain)' },
  { code: 'es-CL', name: 'Spanish (Chile)' },
  { code: 'es-MX', name: 'Spanish (Mexico)' },
  { code: 'en-GB', name: 'English (UK)' },
  { code: 'en-US', name: 'English (US)' },
  { code: 'el-GR', name: 'Greek (Greece)' },
  { code: 'fr-FR', name: 'French (France)' },
  { code: 'de-DE', name: 'German (Germany)' },
  { code: 'it-IT', name: 'Italian (Italy)' },
  { code: 'pt-BR', name: 'Portuguese (Brasil)' }
];

// Mapeo de códigos de idioma a banderas
const LANGUAGE_FLAGS: Record<string, string> = {
  'es-ES': spanish,
  'es-CL': chilean,
  'es-MX': mexican,
  'en-GB': english,
  'en-US': american,
  'el-GR': greek,
  'fr-FR': french,
  'de-DE': german,
  'it-IT': italian,
  'pt-BR': brasilian
};

// Define types for tools
interface ToolItem {
  toolid: string;
  name: string;
  description: string;
  toolgroup: string;
  active: boolean;
  requiredvalidations?: string[];
}

interface AvailableValidation {
  validationid: string;
  name: string;
  description: string;
}

interface AgentConfigColumnProps {
  agentId: string;
  onUpdateSuccess?: (
    field:
      | 'firstMessage'
      | 'inboundFirstMessage'
      | 'prompt'
      | 'inboundprompt'
      | 'callresultprompt'
      | 'tools'
  ) => void;
  onError?: (error: Error) => void;
}

const AgentConfigColumn: React.FC<AgentConfigColumnProps> = ({
  agentId,
  onUpdateSuccess,
  onError
}) => {
  // State for form data
  const [firstMessage, setFirstMessage] = useState('');
  const [initialFirstMessage, setInitialFirstMessage] = useState('');
  const [inboundFirstMessage, setInboundFirstMessage] = useState('');
  const [initialInboundFirstMessage, setInitialInboundFirstMessage] =
    useState('');
  const [prompt, setPrompt] = useState('');
  const [initialPrompt, setInitialPrompt] = useState('');
  const [inboundPrompt, setInboundPrompt] = useState('');
  const [initialInboundPrompt, setInitialInboundPrompt] = useState('');
  const [callResultPrompt, setCallResultPrompt] = useState('');
  const [initialCallResultPrompt, setInitialCallResultPrompt] = useState('');
  const [selectedPromptType, setSelectedPromptType] = useState<PromptType>(
    PromptType.FIRST_MESSAGE
  );

  // State for tools - separados por tipo de llamada
  const [availableTools, setAvailableTools] = useState<ToolItem[]>([]);
  const [outboundTools, setOutboundTools] = useState<ToolItem[]>([]);
  const [inboundTools, setInboundTools] = useState<ToolItem[]>([]);
  const [availableValidations, setAvailableValidations] = useState<
    AvailableValidation[]
  >([]);

  // State for loading/saving states
  const [isSavingFirstMessage, setIsSavingFirstMessage] = useState(false);
  const [isSavingInboundFirstMessage, setIsSavingInboundFirstMessage] =
    useState(false);
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [isSavingInboundPrompt, setIsSavingInboundPrompt] = useState(false);
  const [isSavingCallResultPrompt, setIsSavingCallResultPrompt] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // State for expandable
  const [expandedSection, setExpandedSection] = useState<string | null>(
    `tool-group-REQUEST_INFO`
  );

  // Estado para seguir los cambios en cada sección
  const [hasPendingChanges, setHasPendingChanges] = useState({
    firstMessage: false,
    inboundFirstMessage: false,
    prompt: false,
    inboundprompt: false,
    callresultprompt: false,
    tools: false
  });

  // Estados para variables de outbound greeting
  const [showVariableAutocomplete, setShowVariableAutocomplete] =
    useState(false);
  const [autocompletePosition, setAutocompletePosition] = useState({
    top: 0,
    left: 0
  });
  const [variableSearchTerm, setVariableSearchTerm] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const firstMessageTextareaRef = useRef<HTMLTextAreaElement>(null);

  // Estados para variables de inbound greeting
  const [showInboundVariableAutocomplete, setShowInboundVariableAutocomplete] =
    useState(false);
  const [inboundAutocompletePosition, setInboundAutocompletePosition] =
    useState({
      top: 0,
      left: 0
    });
  const [inboundVariableSearchTerm, setInboundVariableSearchTerm] =
    useState('');
  const [cursorInboundPosition, setCursorInboundPosition] = useState(0);
  const inboundFirstMessageTextareaRef = useRef<HTMLTextAreaElement>(null);
  // Flags para rastrear si cada editor Lexical está en su primera carga
  const isOutboundPromptInitialLoad = useRef(true);
  const isInboundPromptInitialLoad = useRef(true);
  const isCallResultPromptInitialLoad = useRef(true);

  // Estados para adaptadores - Configuraciones disponibles
  const [availableSttConfigs, setAvailableSttConfigs] = useState<
    AIAdapterConfig[]
  >([]);
  const [availableLlmConfigs, setAvailableLlmConfigs] = useState<
    AIAdapterConfig[]
  >([]);
  const [availableTtsConfigs, setAvailableTtsConfigs] = useState<
    AIAdapterConfig[]
  >([]);
  const [availableSummaryConfigs, setAvailableSummaryConfigs] = useState<
    AIAdapterConfig[]
  >([]);

  // IDs seleccionados (los que tiene el agente)
  const [selectedSttConfigId, setSelectedSttConfigId] = useState<number | null>(
    null
  );
  const [selectedLlmConfigId, setSelectedLlmConfigId] = useState<number | null>(
    null
  );
  const [selectedTtsConfigId, setSelectedTtsConfigId] = useState<number | null>(
    null
  );
  const [selectedSummaryConfigId, setSelectedSummaryConfigId] = useState<
    number | null
  >(null);

  // IDs iniciales para detectar cambios
  const [initialSttConfigId, setInitialSttConfigId] = useState<number | null>(
    null
  );
  const [initialLlmConfigId, setInitialLlmConfigId] = useState<number | null>(
    null
  );
  const [initialTtsConfigId, setInitialTtsConfigId] = useState<number | null>(
    null
  );
  const [initialSummaryConfigId, setInitialSummaryConfigId] = useState<
    number | null
  >(null);

  // Estados de guardado
  const [isSavingAdapters, setIsSavingAdapters] = useState(false);
  const [hasPendingAdaptersChanges, setHasPendingAdaptersChanges] =
    useState(false);

  // Campos adicionales del agente
  const [language, setLanguage] = useState('es-ES');
  const [voice, setVoice] = useState('');
  const [initialLanguage, setInitialLanguage] = useState('es-ES');
  const [initialVoice, setInitialVoice] = useState('');

  // Estados para el modal de diagramas
  const [showDiagramModal, setShowDiagramModal] = useState(false);
  const [diagramPromptType, setDiagramPromptType] = useState<
    'outbound' | 'inbound'
  >('outbound');

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<
    'success' | 'error' | 'warning' | 'info'
  >('info');

  // Theme from context
  const {
    config: { isDark }
  } = useAppContext();

  // Helper function to show toast
  const showToastMessage = (
    message: string,
    variant: 'success' | 'error' | 'warning' | 'info'
  ) => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  // Función para detectar si el usuario está escribiendo una variable
  const checkForVariableTrigger = (text: string, cursorPos: number) => {
    // Validar que text no sea null o undefined
    if (!text) {
      return { isTypingVariable: false, searchTerm: '' };
    }

    // Buscar "{{" antes de la posición del cursor
    const textBeforeCursor = text.substring(0, cursorPos);
    const lastOpenBrace = textBeforeCursor.lastIndexOf('{{');

    if (lastOpenBrace !== -1) {
      // Verificar si no hay "}}" después del "{{"
      const textAfterBrace = text.substring(lastOpenBrace);
      const closeIndex = textAfterBrace.indexOf('}}');

      if (closeIndex === -1 || closeIndex > cursorPos - lastOpenBrace) {
        // Estamos dentro de una variable
        const searchTerm = textBeforeCursor.substring(lastOpenBrace + 2);
        return { isTypingVariable: true, searchTerm };
      }
    }

    return { isTypingVariable: false, searchTerm: '' };
  };

  // Función para insertar la variable seleccionada
  const handleVariableSelect = (variableName: string) => {
    if (firstMessageTextareaRef.current) {
      const textarea = firstMessageTextareaRef.current;
      const text = firstMessage;
      const textBeforeCursor = text.substring(0, cursorPosition);
      const lastOpenBrace = textBeforeCursor.lastIndexOf('{{');

      if (lastOpenBrace !== -1) {
        // Reemplazar desde {{ hasta el cursor con la variable completa
        const newText =
          text.substring(0, lastOpenBrace) +
          `{{${variableName}}}` +
          text.substring(cursorPosition);

        setFirstMessage(newText);
        setHasPendingChanges(prev => ({
          ...prev,
          firstMessage: newText !== initialFirstMessage
        }));

        // Posicionar el cursor después de la variable
        const newCursorPos = lastOpenBrace + variableName.length + 4; // 4 = {{}}
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }
    }

    setShowVariableAutocomplete(false);
    setVariableSearchTerm('');
  };

  // Función para insertar la variable seleccionada
  const handleInboundVariableSelect = (variableName: string) => {
    if (inboundFirstMessageTextareaRef.current) {
      const textarea = inboundFirstMessageTextareaRef.current;
      const text = inboundFirstMessage;
      const textBeforeCursor = text.substring(0, cursorInboundPosition);
      const lastOpenBrace = textBeforeCursor.lastIndexOf('{{');

      if (lastOpenBrace !== -1) {
        // Reemplazar desde {{ hasta el cursor con la variable completa
        const newText =
          text.substring(0, lastOpenBrace) +
          `{{${variableName}}}` +
          text.substring(cursorInboundPosition);

        setInboundFirstMessage(newText);
        setHasPendingChanges(prev => ({
          ...prev,
          inboundFirstMessage: newText !== initialInboundFirstMessage
        }));

        // Posicionar el cursor después de la variable
        const newCursorPos = lastOpenBrace + variableName.length + 4; // 4 = {{}}
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(newCursorPos, newCursorPos);
        }, 0);
      }
    }

    setShowInboundVariableAutocomplete(false);
    setInboundVariableSearchTerm('');
  };

  // Función para calcular la posición del autocompletado
  const calculateAutocompletePosition = () => {
    if (firstMessageTextareaRef.current) {
      const textarea = firstMessageTextareaRef.current;

      // Posición relativa al contenedor padre (que ahora tiene position: relative)
      setAutocompletePosition({
        top: textarea.offsetHeight - 25, // Justo debajo del textarea
        left: 20 // Alineado a la izquierda del contenedor
      });
    }
  };

  // Función para calcular la posición del autocompletado
  const calculateInboundAutocompletePosition = () => {
    if (inboundFirstMessageTextareaRef.current) {
      const textarea = inboundFirstMessageTextareaRef.current;

      // Posición relativa al contenedor padre (que ahora tiene position: relative)
      setInboundAutocompletePosition({
        top: textarea.offsetHeight - 25, // Justo debajo del textarea
        left: 20 // Alineado a la izquierda del contenedor
      });
    }
  };

  // Fetch agent data, tools, all available tools, and validations on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);

        // Obtener datos del agente
        try {
          const agentData = await getAgentData(agentId);
          setFirstMessage(agentData.firstmessage || '');
          setInitialFirstMessage(agentData.firstmessage || '');
          setInboundFirstMessage(agentData.inboundfirstmessage || '');
          setInitialInboundFirstMessage(agentData.inboundfirstmessage || '');
          setPrompt(agentData.prompt || '');
          setInitialPrompt(agentData.prompt || '');
          setInboundPrompt(agentData.inboundprompt || '');
          setInitialInboundPrompt(agentData.inboundprompt || '');
          setCallResultPrompt(agentData.callresultprompt || '');
          setInitialCallResultPrompt(agentData.callresultprompt || '');

          // Cargar language y voice
          setLanguage(agentData.language || 'es-ES');
          setInitialLanguage(agentData.language || 'es-ES');
          setVoice(agentData.voice || '');
          setInitialVoice(agentData.voice || '');

          // Cargar IDs de configuraciones de adaptadores
          setSelectedSttConfigId(agentData.sttConfigId || null);
          setInitialSttConfigId(agentData.sttConfigId || null);

          setSelectedLlmConfigId(agentData.llmConfigId || null);
          setInitialLlmConfigId(agentData.llmConfigId || null);

          setSelectedTtsConfigId(agentData.ttsConfigId || null);
          setInitialTtsConfigId(agentData.ttsConfigId || null);

          setSelectedSummaryConfigId(agentData.summaryConfigId || null);
          setInitialSummaryConfigId(agentData.summaryConfigId || null);
        } catch (error) {
          showToastMessage('Error fetching agent data', 'error');
          console.error('Error fetching agent data:', error);
          throw error;
        }

        // Cargar todas las configuraciones disponibles de adaptadores
        try {
          const [sttConfigs, llmConfigs, ttsConfigs, summaryConfigs] =
            await Promise.all([
              getAllSttConfigs(),
              getAllLlmConfigs(),
              getAllTtsConfigs(),
              getAllSummaryConfigs()
            ]);

          setAvailableSttConfigs(sttConfigs);
          setAvailableLlmConfigs(llmConfigs);
          setAvailableTtsConfigs(ttsConfigs);
          setAvailableSummaryConfigs(summaryConfigs);

          console.log('Available adapter configurations loaded:', {
            stt: sttConfigs.length,
            llm: llmConfigs.length,
            tts: ttsConfigs.length,
            summary: summaryConfigs.length
          });
        } catch (error) {
          console.error(
            'Error loading available adapter configurations:',
            error
          );
          showToastMessage('Error loading adapter configurations', 'error');
        }

        // Get tools associated with this agent for both call types
        try {
          const [outboundToolsData, inboundToolsData] = await Promise.all([
            getAgentTools(agentId, 'Outbound'),
            getAgentTools(agentId, 'Inbound')
          ]);

          // Parsear requiredvalidations si viene como string
          const fixValidations = (tools: ToolItem[]) => {
            return tools.map(tool => ({
              ...tool,
              requiredvalidations:
                typeof tool.requiredvalidations === 'string'
                  ? JSON.parse(tool.requiredvalidations)
                  : tool.requiredvalidations || []
            }));
          };

          setOutboundTools(fixValidations(outboundToolsData));
          setInboundTools(fixValidations(inboundToolsData));
        } catch (error) {
          console.error('Error fetching agent tools:', error);
          showToastMessage('Error fetching agent tools', 'error');
        }

        // Get all available tools
        const availableToolsData = await getAvailableTools();
        setAvailableTools(availableToolsData);

        // Get all available validations
        const validationsData = await getAgentValidations();
        setAvailableValidations(validationsData);

        // Asegurarnos de que el primer grupo esté seleccionado al cargar
        setExpandedSection(`tool-group-REQUEST_INFO`);

        setIsLoading(false);
      } catch (error) {
        showToastMessage('Error fetching agent configuration data', 'error');
        console.error('Error fetching agent configuration data:', error);
        if (onError) {
          onError(
            error instanceof Error
              ? error
              : new Error('Failed to fetch agent data')
          );
        }
        setIsLoading(false);
      }
    };

    fetchData();
  }, [agentId]);

  // Detectar cambios en las configuraciones de adaptadores
  const checkAdaptersChanges = (
    newSttId: number | null,
    newLlmId: number | null,
    newTtsId: number | null,
    newSummaryId: number | null,
    newLanguage: string,
    newVoice: string
  ) => {
    const hasChanges =
      newSttId !== initialSttConfigId ||
      newLlmId !== initialLlmConfigId ||
      newTtsId !== initialTtsConfigId ||
      newSummaryId !== initialSummaryConfigId ||
      newLanguage !== initialLanguage ||
      newVoice !== initialVoice;

    setHasPendingAdaptersChanges(hasChanges);
  };

  // Handler para cambio de STT Config
  const handleSttConfigChange = (configId: number | null) => {
    setSelectedSttConfigId(configId);
    checkAdaptersChanges(
      configId,
      selectedLlmConfigId,
      selectedTtsConfigId,
      selectedSummaryConfigId,
      language,
      voice
    );
  };

  // Handler para cambio de LLM Config
  const handleLlmConfigChange = (configId: number | null) => {
    setSelectedLlmConfigId(configId);
    checkAdaptersChanges(
      selectedSttConfigId,
      configId,
      selectedTtsConfigId,
      selectedSummaryConfigId,
      language,
      voice
    );
  };

  // Handler para cambio de TTS Config
  const handleTtsConfigChange = (configId: number | null) => {
    setSelectedTtsConfigId(configId);
    checkAdaptersChanges(
      selectedSttConfigId,
      selectedLlmConfigId,
      configId,
      selectedSummaryConfigId,
      language,
      voice
    );
  };

  // Handler para cambio de Summary Config
  const handleSummaryConfigChange = (configId: number | null) => {
    setSelectedSummaryConfigId(configId);
    checkAdaptersChanges(
      selectedSttConfigId,
      selectedLlmConfigId,
      selectedTtsConfigId,
      configId,
      language,
      voice
    );
  };

  // Handler para cambio de Language
  const handleLanguageChange = (newLanguage: string) => {
    setLanguage(newLanguage);
    checkAdaptersChanges(
      selectedSttConfigId,
      selectedLlmConfigId,
      selectedTtsConfigId,
      selectedSummaryConfigId,
      newLanguage,
      voice
    );
  };

  // Handler para cambio de Voice
  const handleVoiceChange = (newVoice: string) => {
    setVoice(newVoice);
    checkAdaptersChanges(
      selectedSttConfigId,
      selectedLlmConfigId,
      selectedTtsConfigId,
      selectedSummaryConfigId,
      language,
      newVoice
    );
  };

  // Resetear configuraciones de adaptadores
  const resetAdaptersConfig = () => {
    setSelectedSttConfigId(initialSttConfigId);
    setSelectedLlmConfigId(initialLlmConfigId);
    setSelectedTtsConfigId(initialTtsConfigId);
    setSelectedSummaryConfigId(initialSummaryConfigId);
    setLanguage(initialLanguage);
    setVoice(initialVoice);
    setHasPendingAdaptersChanges(false);
  };

  // Guardar configuraciones de adaptadores
  const saveAdaptersConfig = async () => {
    try {
      setIsSavingAdapters(true);

      // Actualizar el agente con los nuevos IDs de configuraciones
      await updateAgent(agentId, {
        sttConfigId: selectedSttConfigId,
        llmConfigId: selectedLlmConfigId,
        ttsConfigId: selectedTtsConfigId,
        summaryConfigId: selectedSummaryConfigId,
        language: language,
        voice: voice
      });

      // Actualizar los valores iniciales
      setInitialSttConfigId(selectedSttConfigId);
      setInitialLlmConfigId(selectedLlmConfigId);
      setInitialTtsConfigId(selectedTtsConfigId);
      setInitialSummaryConfigId(selectedSummaryConfigId);
      setInitialLanguage(language);
      setInitialVoice(voice);

      setHasPendingAdaptersChanges(false);

      if (onUpdateSuccess) {
        onUpdateSuccess('tools');
      }
    } catch (error) {
      console.error('Error saving adapter configurations:', error);
      showToastMessage('Error saving adapter configurations', 'error');

      if (onError) {
        onError(
          error instanceof Error
            ? error
            : new Error('Failed to save adapter configurations')
        );
      }
    } finally {
      setIsSavingAdapters(false);
    }
  };

  // Handle outbound greeting change
  const handleFirstMessageChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value;
    const newCursorPos = e.target.selectionStart || 0;

    setFirstMessage(newValue);
    setCursorPosition(newCursorPos);

    setHasPendingChanges(prev => ({
      ...prev,
      firstMessage: newValue !== initialFirstMessage
    }));

    // Verificar si estamos escribiendo una variable
    const { isTypingVariable, searchTerm } = checkForVariableTrigger(
      newValue,
      newCursorPos
    );

    if (isTypingVariable) {
      setVariableSearchTerm(searchTerm);
      setShowVariableAutocomplete(true);
      calculateAutocompletePosition();
    } else {
      setShowVariableAutocomplete(false);
      setVariableSearchTerm('');
    }
  };

  // Handle inbound greeting change
  const handleInboundFirstMessageChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value || '';
    const newCursorPos = e.target.selectionStart || 0;

    setInboundFirstMessage(newValue);
    setCursorInboundPosition(newCursorPos);

    setHasPendingChanges(prev => ({
      ...prev,
      inboundFirstMessage: newValue !== (initialInboundFirstMessage || '')
    }));

    // Verificar si estamos escribiendo una variable
    const { isTypingVariable, searchTerm } = checkForVariableTrigger(
      newValue,
      newCursorPos
    );

    if (isTypingVariable) {
      setInboundVariableSearchTerm(searchTerm);
      setShowInboundVariableAutocomplete(true);
      calculateInboundAutocompletePosition();
    } else {
      setShowInboundVariableAutocomplete(false);
      setInboundVariableSearchTerm('');
    }
  };

  // Manejar cambios en la posición del cursor Outbound
  const handleCursorChange = (e: React.SyntheticEvent<HTMLTextAreaElement>) => {
    const textarea = e.target as HTMLTextAreaElement;
    const newCursorPos = textarea.selectionStart || 0;
    setCursorPosition(newCursorPos);

    // Verificar si el cursor está en una posición de variable
    const { isTypingVariable, searchTerm } = checkForVariableTrigger(
      firstMessage,
      newCursorPos
    );

    if (isTypingVariable) {
      setVariableSearchTerm(searchTerm);
      setShowVariableAutocomplete(true);
      calculateAutocompletePosition();
    } else {
      setShowVariableAutocomplete(false);
      setVariableSearchTerm('');
    }
  };

  // Manejar cambios en la posición del cursor Inbound
  const handleInboundCursorChange = (
    e: React.SyntheticEvent<HTMLTextAreaElement>
  ) => {
    const textarea = e.target as HTMLTextAreaElement;
    const newCursorPos = textarea.selectionStart || 0;
    setCursorInboundPosition(newCursorPos);

    // Verificar si el cursor está en una posición de variable
    const { isTypingVariable, searchTerm } = checkForVariableTrigger(
      inboundFirstMessage || '',
      newCursorPos
    );

    if (isTypingVariable) {
      setInboundVariableSearchTerm(searchTerm);
      setShowInboundVariableAutocomplete(true);
      calculateInboundAutocompletePosition();
    } else {
      setShowInboundVariableAutocomplete(false);
      setInboundVariableSearchTerm('');
    }
  };

  // Save outbound greeting to database
  const saveFirstMessage = async () => {
    try {
      setIsSavingFirstMessage(true);
      await updateAgentFirstMessage(agentId, firstMessage);

      setInitialFirstMessage(firstMessage);

      if (onUpdateSuccess) {
        onUpdateSuccess('firstMessage');
      }

      // Actualizar el estado para reflejar que ya no hay cambios pendientes
      setHasPendingChanges(prev => ({
        ...prev,
        firstMessage: false
      }));
    } catch (error) {
      console.error('Error saving Outbound Greeting:', error);
      if (onError) {
        onError(
          error instanceof Error
            ? error
            : new Error('Failed to save Outbound Greeting:')
        );
      }
    } finally {
      setIsSavingFirstMessage(false);
    }
  };

  // Save inbound greeting to database
  const saveInboundFirstMessage = async () => {
    try {
      setIsSavingInboundFirstMessage(true);
      await updateAgentInboundFirstMessage(agentId, inboundFirstMessage);

      setInitialInboundFirstMessage(inboundFirstMessage);

      if (onUpdateSuccess) {
        onUpdateSuccess('inboundFirstMessage');
      }

      // Actualizar el estado para reflejar que ya no hay cambios pendientes
      setHasPendingChanges(prev => ({
        ...prev,
        inboundFirstMessage: false
      }));
    } catch (error) {
      console.error('Error saving Inbound Greeting:', error);
      if (onError) {
        onError(
          error instanceof Error
            ? error
            : new Error('Failed to save Inbound Greeting:')
        );
      }
    } finally {
      setIsSavingInboundFirstMessage(false);
    }
  };

  // Save prompt to database
  const savePrompt = async () => {
    try {
      setIsSavingPrompt(true);
      await updateAgentPrompt(agentId, prompt);

      if (onUpdateSuccess) {
        onUpdateSuccess('prompt');
      }

      setInitialPrompt(prompt);

      // Actualizar el estado para reflejar que ya no hay cambios pendientes
      setHasPendingChanges(prev => ({
        ...prev,
        prompt: false
      }));
    } catch (error) {
      console.error('Error saving prompt:', error);
      if (onError) {
        onError(
          error instanceof Error ? error : new Error('Failed to save prompt')
        );
      }
    } finally {
      setIsSavingPrompt(false);
    }
  };

  // Save inbound prompt to database
  const saveInboundPrompt = async () => {
    try {
      setIsSavingInboundPrompt(true);
      await updateAgentInboundPrompt(agentId, inboundPrompt);

      if (onUpdateSuccess) {
        onUpdateSuccess('inboundprompt');
      }

      setInitialInboundPrompt(inboundPrompt);
      setHasPendingChanges(prev => ({
        ...prev,
        inboundprompt: false
      }));
    } catch (error) {
      console.error('Error saving inbound prompt:', error);
      if (onError) {
        onError(
          error instanceof Error
            ? error
            : new Error('Failed to save inbound prompt')
        );
      }
    } finally {
      setIsSavingInboundPrompt(false);
    }
  };

  // Save call result prompt to database
  const saveCallResultPrompt = async () => {
    try {
      setIsSavingCallResultPrompt(true);
      await updateAgentCallResultPrompt(agentId, callResultPrompt);

      if (onUpdateSuccess) {
        onUpdateSuccess('callresultprompt');
      }

      setInitialCallResultPrompt(callResultPrompt);

      // Actualizar el estado para reflejar que ya no hay cambios pendientes
      setHasPendingChanges(prev => ({
        ...prev,
        callresultprompt: false
      }));
    } catch (error) {
      console.error('Error saving call result prompt:', error);
      if (onError) {
        onError(
          error instanceof Error
            ? error
            : new Error('Failed to save call result prompt')
        );
      }
    } finally {
      setIsSavingCallResultPrompt(false);
    }
  };

  // Toggle tool selection - con funciones individuales para mejor control
  const toggleToolSelection = async (
    toolId: string,
    isChecked: boolean,
    calltype: 'Outbound' | 'Inbound'
  ) => {
    try {
      if (isChecked) {
        const toolToAdd = availableTools.find(tool => tool.toolid === toolId);
        if (toolToAdd) {
          await assignToolToAgent(agentId, toolId, calltype, []);

          // Actualizar el estado correspondiente
          if (calltype === 'Outbound') {
            setOutboundTools(prev => [
              ...prev,
              { ...toolToAdd, requiredvalidations: [] }
            ]);
          } else {
            setInboundTools(prev => [
              ...prev,
              { ...toolToAdd, requiredvalidations: [] }
            ]);
          }
        }
      } else {
        await removeToolFromAgent(agentId, calltype, toolId);

        // Actualizar el estado correspondiente
        if (calltype === 'Outbound') {
          setOutboundTools(prev => prev.filter(tool => tool.toolid !== toolId));
        } else {
          setInboundTools(prev => prev.filter(tool => tool.toolid !== toolId));
        }
      }

      setHasPendingChanges(prev => ({
        ...prev,
        tools: true
      }));
    } catch (error) {
      console.error(
        `Error ${isChecked ? 'assigning' : 'removing'} tool:`,
        error
      );
      if (onError) {
        onError(
          error instanceof Error
            ? error
            : new Error(`Failed to ${isChecked ? 'assign' : 'remove'} tool`)
        );
      }
    }
  };

  // Toggle validation requirement for a tool
  const toggleValidationForTool = async (
    toolId: string,
    validationId: string,
    isChecked: boolean,
    calltype: 'Outbound' | 'Inbound'
  ) => {
    try {
      const tools = calltype === 'Outbound' ? outboundTools : inboundTools;
      const tool = tools.find(t => t.toolid === toolId);
      if (!tool) return;

      const currentValidations = tool.requiredvalidations || [];
      const updatedValidations = isChecked
        ? [...currentValidations, validationId]
        : currentValidations.filter(id => id !== validationId);

      await updateToolValidations(
        agentId,
        calltype,
        toolId,
        updatedValidations
      );

      // Actualizar el estado correspondiente
      const setTools =
        calltype === 'Outbound' ? setOutboundTools : setInboundTools;
      setTools(prev =>
        prev.map(t => {
          if (t.toolid === toolId) {
            return { ...t, requiredvalidations: updatedValidations };
          }
          return t;
        })
      );

      setHasPendingChanges(prev => ({
        ...prev,
        tools: true
      }));
    } catch (error) {
      console.error('Error updating validation requirements:', error);
      if (onError) {
        onError(
          error instanceof Error
            ? error
            : new Error('Failed to update validation requirements')
        );
      }
    }
  };

  // Reset outbound greeting to initial value
  const resetFirstMessage = () => {
    setFirstMessage(initialFirstMessage);
    setHasPendingChanges(prev => ({
      ...prev,
      firstMessage: false
    }));
  };

  // Reset inbound greeting to initial value
  const resetInboundFirstMessage = () => {
    setInboundFirstMessage(initialInboundFirstMessage);
    setHasPendingChanges(prev => ({
      ...prev,
      inboundFirstMessage: false
    }));
  };

  // Reset prompt to initial value
  const resetPrompt = () => {
    setPrompt(initialPrompt);
    setHasPendingChanges(prev => ({
      ...prev,
      prompt: false
    }));
  };

  // Reset inbound prompt to initial value
  const resetInboundPrompt = () => {
    setInboundPrompt(initialInboundPrompt);
    setHasPendingChanges(prev => ({
      ...prev,
      inboundprompt: false
    }));
  };

  // Reset call result prompt to initial value
  const resetCallResultPrompt = () => {
    setCallResultPrompt(initialCallResultPrompt);
    setHasPendingChanges(prev => ({
      ...prev,
      callresultprompt: false
    }));
  };

  // Group tools by their type for better organization
  const groupedAvailableTools = availableTools.reduce(
    (acc, tool) => {
      const group = tool.toolgroup || 'Other';
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(tool);
      return acc;
    },
    {} as Record<string, ToolItem[]>
  );

  // Function to check if a tool is currently assigned to the agent
  const isToolAssigned = (toolId: string, calltype: 'Outbound' | 'Inbound') => {
    const tools = calltype === 'Outbound' ? outboundTools : inboundTools;
    return tools.some(tool => tool.toolid === toolId);
  };

  // Function to get validations required for a tool
  const getToolValidations = (
    toolId: string,
    calltype: 'Outbound' | 'Inbound'
  ) => {
    const tools = calltype === 'Outbound' ? outboundTools : inboundTools;
    const tool = tools.find(t => t.toolid === toolId);
    return tool?.requiredvalidations || [];
  };

  // Manejar el cambio de sección en herramientas
  const handleToolGroupChange = (groupKey: string) => {
    setExpandedSection(`tool-group-${groupKey}`);
  };

  // Manejar el cambio entre tipos de prompt
  const handlePromptTypeChange = (promptType: PromptType) => {
    // Verificar cambios sin guardar...

    // Limpiar TODOS los estados de autocompletado, no solo el de outbound
    setShowVariableAutocomplete(false);
    setVariableSearchTerm('');
    setCursorPosition(0);
    setShowInboundVariableAutocomplete(false);
    setInboundVariableSearchTerm('');
    setCursorInboundPosition(0);

    // Desenfocar cualquier textarea activo
    if (firstMessageTextareaRef.current) {
      firstMessageTextareaRef.current.blur();
    }
    if (inboundFirstMessageTextareaRef.current) {
      inboundFirstMessageTextareaRef.current.blur();
    }

    // Resetear el flag correspondiente cuando cambiamos de prompt
    // para que ignore la primera carga del nuevo editor
    if (promptType === PromptType.OUTBOUND) {
      isOutboundPromptInitialLoad.current = true;
    } else if (promptType === PromptType.INBOUND) {
      isInboundPromptInitialLoad.current = true;
    } else if (promptType === PromptType.CALL_RESULT) {
      isCallResultPromptInitialLoad.current = true;
    }

    setSelectedPromptType(promptType);
  };

  return (
    <div className="agent-config-column">
      {/* Toast Notification */}
      <HVAToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />
      {isLoading ? (
        <div className="text-center my-4">
          <HVASpinner />
        </div>
      ) : (
        <div>
          {/* Configuración de IA */}
          <div>
            <h5 className="pb-0 pt-2">Cognitive Engine Settings</h5>
            <p className="fs-9 lh-sm mb-3">
              Configure the language, voice and AI adapters for the agent.
            </p>

            <div className="p-0">
              <div className="d-flex gap-2 mb-2">
                {/* Language */}
                <div className="flex-grow-1 position-relative">
                  <img
                    src={LANGUAGE_FLAGS[language] || spanish}
                    alt="Language flag"
                    style={{
                      width: 'auto',
                      height: '50px',
                      position: 'absolute',
                      left: '1px',
                      top: '1px',
                      zIndex: 10,
                      pointerEvents: 'none'
                    }}
                  />
                  <Form.Floating className="flex-grow-1">
                    <Form.Select
                      className="bg-body"
                      style={{ paddingLeft: '35px' }}
                      value={language}
                      onChange={e => handleLanguageChange(e.target.value)}
                    >
                      {AVAILABLE_LANGUAGES.map(lang => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                    </Form.Select>
                    <label style={{ paddingLeft: '40px' }}>Language</label>
                  </Form.Floating>
                </div>

                {/* Voice */}
                <Form.Floating className="flex-grow-1 col-5">
                  <Form.Control
                    className="bg-body"
                    type="text"
                    value={voice}
                    onChange={e => handleVoiceChange(e.target.value)}
                    placeholder="e.g., es-ES-Standard-A"
                  />
                  <label>Voice ID</label>
                </Form.Floating>
              </div>

              <div className="d-flex gap-2 mb-2">
                {/* STT Adapter */}
                <InputGroup>
                  <InputGroup.Text>
                    <UilEar size={24} />
                  </InputGroup.Text>
                  <Form.Floating className="flex-grow-1">
                    <Form.Select
                      className="bg-body"
                      value={selectedSttConfigId || ''}
                      onChange={e =>
                        handleSttConfigChange(
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                    >
                      <option value="">None</option>
                      {availableSttConfigs.map(config => (
                        <option key={config.id} value={config.id}>
                          {config.configName} ({config.adapterCode})
                        </option>
                      ))}
                    </Form.Select>
                    <label>STT Adapter</label>
                  </Form.Floating>
                </InputGroup>

                {/* LLM Adapter */}
                <InputGroup>
                  <InputGroup.Text>
                    <UilBrain size={24} />
                  </InputGroup.Text>
                  <Form.Floating className="flex-grow-1">
                    <Form.Select
                      className="bg-body"
                      value={selectedLlmConfigId || ''}
                      onChange={e =>
                        handleLlmConfigChange(
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                    >
                      <option value="">None</option>
                      {availableLlmConfigs.map(config => (
                        <option key={config.id} value={config.id}>
                          {config.configName} ({config.adapterCode})
                        </option>
                      ))}
                    </Form.Select>
                    <label>LLM Adapter</label>
                  </Form.Floating>
                </InputGroup>

                {/* TTS Adapter */}
                <InputGroup>
                  <InputGroup.Text>
                    <UilCommentDots size={24} />
                  </InputGroup.Text>
                  <Form.Floating className="flex-grow-1">
                    <Form.Select
                      className="bg-body"
                      value={selectedTtsConfigId || ''}
                      onChange={e =>
                        handleTtsConfigChange(
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                    >
                      <option value="">None</option>
                      {availableTtsConfigs.map(config => (
                        <option key={config.id} value={config.id}>
                          {config.configName} ({config.adapterCode})
                        </option>
                      ))}
                    </Form.Select>
                    <label>TTS Adapter</label>
                  </Form.Floating>
                </InputGroup>

                {/* Summary Adapter */}
                <InputGroup>
                  <InputGroup.Text>
                    <UilFileLandscapeAlt size={24} />
                  </InputGroup.Text>
                  <Form.Floating className="flex-grow-1">
                    <Form.Select
                      className="bg-body"
                      value={selectedSummaryConfigId || ''}
                      onChange={e =>
                        handleSummaryConfigChange(
                          e.target.value ? parseInt(e.target.value) : null
                        )
                      }
                    >
                      <option value="">None</option>
                      {availableSummaryConfigs.map(config => (
                        <option key={config.id} value={config.id}>
                          {config.configName} ({config.adapterCode})
                        </option>
                      ))}
                    </Form.Select>
                    <label>Summary and analysis Adapter</label>
                  </Form.Floating>
                </InputGroup>
              </div>
            </div>

            {/* Botones de guardar/resetear */}
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="phoenix-secondary"
                size="sm"
                onClick={resetAdaptersConfig}
                disabled={!hasPendingAdaptersChanges}
                className="me-2"
              >
                <FontAwesomeIcon icon={faUndo} className="me-1" />
                Reset
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={saveAdaptersConfig}
                disabled={!hasPendingAdaptersChanges || isSavingAdapters}
              >
                {isSavingAdapters ? (
                  <>
                    <FontAwesomeIcon icon={faC} className="me-1" spin />
                    Saving...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faPen} className="me-1" />
                    Save AI Configuration
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Agent Instructions (Prompt) Section with tabs */}
          <div>
            <h5 className="pb-0 pt-2">Agent Instructions (Prompt)</h5>
            {/* Botones de selección de tipo de prompt */}
            <div className="mb-0">
              <p className="fs-9 lh-sm">
                These instructions define how the agent behaves, and how they
                handle different scenarios.
              </p>
              <Button
                variant={
                  selectedPromptType === PromptType.FIRST_MESSAGE
                    ? 'secondary'
                    : 'phoenix-secondary'
                }
                className={
                  selectedPromptType === PromptType.FIRST_MESSAGE
                    ? 'bg-body-quaternary me-2 mb-2'
                    : 'me-2 mb-2'
                }
                size="sm"
                onClick={() => handlePromptTypeChange(PromptType.FIRST_MESSAGE)}
              >
                Outbound Greeting
                {hasPendingChanges.firstMessage && (
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    className="ms-2 text-danger"
                    title="Unsaved changes"
                  />
                )}
              </Button>
              <Button
                variant={
                  selectedPromptType === PromptType.INBOUND_FIRST_MESSAGE
                    ? 'secondary'
                    : 'phoenix-secondary'
                }
                className={
                  selectedPromptType === PromptType.INBOUND_FIRST_MESSAGE
                    ? 'bg-body-quaternary me-2 mb-2'
                    : 'me-2 mb-2'
                }
                size="sm"
                onClick={() =>
                  handlePromptTypeChange(PromptType.INBOUND_FIRST_MESSAGE)
                }
              >
                Inbound Greeting
                {hasPendingChanges.inboundFirstMessage && (
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    className="ms-2 text-danger"
                    title="Unsaved changes"
                  />
                )}
              </Button>
              <Button
                variant={
                  selectedPromptType === PromptType.OUTBOUND
                    ? 'secondary'
                    : 'phoenix-secondary'
                }
                className={
                  selectedPromptType === PromptType.OUTBOUND
                    ? 'bg-body-quaternary me-2 mb-2'
                    : 'me-2 mb-2'
                }
                size="sm"
                onClick={() => handlePromptTypeChange(PromptType.OUTBOUND)}
              >
                Outbound Prompt
                <FontAwesomeIcon
                  icon={faProjectDiagram}
                  className="ms-2 text-secondary-light"
                  title="View Flow Diagram"
                  onClick={e => {
                    e.stopPropagation(); // Prevenir que se cambie el tab
                    setDiagramPromptType('outbound');
                    setShowDiagramModal(true);
                  }}
                  style={{ cursor: 'pointer' }}
                />
                {hasPendingChanges.prompt && (
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    className="ms-2 text-danger"
                    title="Unsaved changes"
                  />
                )}
              </Button>
              <Button
                variant={
                  selectedPromptType === PromptType.INBOUND
                    ? 'secondary'
                    : 'phoenix-secondary'
                }
                className={
                  selectedPromptType === PromptType.INBOUND
                    ? 'bg-body-quaternary me-2 mb-2'
                    : 'me-2 mb-2'
                }
                size="sm"
                onClick={() => handlePromptTypeChange(PromptType.INBOUND)}
              >
                Inbound Prompt
                <FontAwesomeIcon
                  icon={faProjectDiagram}
                  className="ms-2 text-secondary-light"
                  title="View Flow Diagram"
                  onClick={e => {
                    e.stopPropagation(); // Prevenir que se cambie el tab
                    setDiagramPromptType('inbound');
                    setShowDiagramModal(true);
                  }}
                  style={{ cursor: 'pointer' }}
                />
                {hasPendingChanges.inboundprompt && (
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    className="ms-2 text-danger"
                    title="Unsaved changes"
                  />
                )}
              </Button>
              <Button
                variant={
                  selectedPromptType === PromptType.CALL_RESULT
                    ? 'secondary'
                    : 'phoenix-secondary'
                }
                className={
                  selectedPromptType === PromptType.CALL_RESULT
                    ? 'bg-body-quaternary me-2 mb-2'
                    : 'me-2 mb-2'
                }
                size="sm"
                onClick={() => handlePromptTypeChange(PromptType.CALL_RESULT)}
              >
                Call Result Prompt
                {hasPendingChanges.callresultprompt && (
                  <FontAwesomeIcon
                    icon={faExclamationCircle}
                    className="ms-2 text-danger"
                    title="Unsaved changes"
                  />
                )}
              </Button>
            </div>

            <div>
              {selectedPromptType === PromptType.FIRST_MESSAGE ? (
                <>
                  {/* Agregar contenedor con position relative */}
                  <div style={{ position: 'relative' }}>
                    <Form.Group className="mb-3">
                      <Form.Control
                        ref={firstMessageTextareaRef}
                        as="textarea"
                        value={firstMessage}
                        onChange={handleFirstMessageChange}
                        onKeyUp={(e: any) => handleCursorChange(e)}
                        onClick={(e: any) => handleCursorChange(e)}
                        rows={4}
                        className="scrollbar bg-body shadow-none"
                        placeholder="Type {{ to see availables variables"
                      />
                    </Form.Group>

                    {/* VariableAutocomplete dentro del contenedor relativo */}
                    <VariableAutocomplete
                      show={showVariableAutocomplete}
                      position={autocompletePosition}
                      searchTerm={variableSearchTerm}
                      onSelect={handleVariableSelect}
                      onHide={() => setShowVariableAutocomplete(false)}
                    />
                  </div>
                  <div className="d-flex justify-content-end">
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      onClick={resetFirstMessage}
                      disabled={!hasPendingChanges.firstMessage}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faUndo} className="me-1" />
                      Reset
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={saveFirstMessage}
                      disabled={
                        !hasPendingChanges.firstMessage || isSavingFirstMessage
                      }
                    >
                      {isSavingFirstMessage ? (
                        <>
                          <FontAwesomeIcon icon={faC} className="me-1" spin />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPen} className="me-1" />
                          Save Outbound Greeting
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : selectedPromptType === PromptType.INBOUND_FIRST_MESSAGE ? (
                <>
                  {/* Agregar contenedor con position relative */}
                  <div style={{ position: 'relative' }}>
                    <Form.Group className="mb-3">
                      <Form.Control
                        ref={inboundFirstMessageTextareaRef}
                        as="textarea"
                        value={inboundFirstMessage || ''}
                        onChange={handleInboundFirstMessageChange}
                        onKeyUp={(e: any) => handleInboundCursorChange(e)}
                        onClick={(e: any) => handleInboundCursorChange(e)}
                        rows={4}
                        className="scrollbar bg-body shadow-none"
                        placeholder="Type {{ to see availables variables"
                      />
                    </Form.Group>

                    {/* VariableAutocomplete dentro del contenedor relativo */}
                    <VariableAutocomplete
                      show={showInboundVariableAutocomplete}
                      position={inboundAutocompletePosition}
                      searchTerm={inboundVariableSearchTerm}
                      onSelect={handleInboundVariableSelect}
                      onHide={() => setShowInboundVariableAutocomplete(false)}
                    />
                  </div>
                  <div className="d-flex justify-content-end">
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      onClick={resetInboundFirstMessage}
                      disabled={!hasPendingChanges.inboundFirstMessage}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faUndo} className="me-1" />
                      Reset
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={saveInboundFirstMessage}
                      disabled={
                        !hasPendingChanges.inboundFirstMessage ||
                        isSavingInboundFirstMessage
                      }
                    >
                      {isSavingInboundFirstMessage ? (
                        <>
                          <FontAwesomeIcon icon={faC} className="me-1" spin />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPen} className="me-1" />
                          Save Inbound Greeting
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : selectedPromptType === PromptType.OUTBOUND ? (
                <>
                  <LexicalPromptEditor
                    key="outbound-prompt-editor"
                    initialValue={prompt}
                    onChange={markdown => {
                      if (isOutboundPromptInitialLoad.current) {
                        isOutboundPromptInitialLoad.current = false;
                        return;
                      }
                      setPrompt(markdown);
                      setHasPendingChanges(prev => ({
                        ...prev,
                        prompt: markdown !== initialPrompt
                      }));
                    }}
                    placeholder="Start writing your agent instructions..."
                    isDarkMode={isDark}
                    height={435}
                  />
                  <div className="d-flex justify-content-end">
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      onClick={resetPrompt}
                      disabled={!hasPendingChanges.prompt}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faUndo} className="me-1" />
                      Reset
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={savePrompt}
                      disabled={!hasPendingChanges.prompt || isSavingPrompt}
                    >
                      {isSavingPrompt ? (
                        <>
                          <FontAwesomeIcon icon={faC} className="me-1" spin />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPen} className="me-1" />
                          Save Outbound Prompt
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : selectedPromptType === PromptType.INBOUND ? (
                <>
                  <LexicalPromptEditor
                    key="inbound-prompt-editor"
                    initialValue={inboundPrompt}
                    onChange={markdown => {
                      if (isInboundPromptInitialLoad.current) {
                        isInboundPromptInitialLoad.current = false;
                        return;
                      }
                      setInboundPrompt(markdown);
                      setHasPendingChanges(prev => ({
                        ...prev,
                        inboundprompt: markdown !== initialInboundPrompt
                      }));
                    }}
                    placeholder="Start writing your agent instructions..."
                    isDarkMode={isDark}
                    height={435}
                  />
                  <div className="d-flex justify-content-end">
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      onClick={resetInboundPrompt}
                      disabled={!hasPendingChanges.inboundprompt}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faUndo} className="me-1" />
                      Reset
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={saveInboundPrompt}
                      disabled={
                        !hasPendingChanges.inboundprompt ||
                        isSavingInboundPrompt
                      }
                    >
                      {isSavingInboundPrompt ? (
                        <>
                          <FontAwesomeIcon icon={faC} className="me-1" spin />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPen} className="me-1" />
                          Save Inbound Prompt
                        </>
                      )}
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <LexicalPromptEditor
                    key="callresult-prompt-editor"
                    initialValue={callResultPrompt}
                    onChange={markdown => {
                      if (isCallResultPromptInitialLoad.current) {
                        isCallResultPromptInitialLoad.current = false;
                        return;
                      }
                      setCallResultPrompt(markdown);
                      setHasPendingChanges(prev => ({
                        ...prev,
                        callresultprompt: markdown !== initialCallResultPrompt
                      }));
                    }}
                    placeholder="Start writing your agent instructions..."
                    isDarkMode={isDark}
                    height={435}
                  />
                  <div className="d-flex justify-content-end">
                    <Button
                      variant="phoenix-secondary"
                      size="sm"
                      onClick={resetCallResultPrompt}
                      disabled={!hasPendingChanges.callresultprompt}
                      className="me-2"
                    >
                      <FontAwesomeIcon icon={faUndo} className="me-1" />
                      Reset
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={saveCallResultPrompt}
                      disabled={
                        !hasPendingChanges.callresultprompt ||
                        isSavingCallResultPrompt
                      }
                    >
                      {isSavingCallResultPrompt ? (
                        <>
                          <FontAwesomeIcon icon={faC} className="me-1" spin />
                          Saving...
                        </>
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faPen} className="me-1" />
                          Save Call Result Prompt
                        </>
                      )}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Agent Tools Section */}
          <div className="mb-3">
            <h5 className="pb-0 pt-2">Agent Tools</h5>
            <div>
              <p className="fs-9 lh-sm">
                Select the tools that the agent can use during the conversation.
                For each tool, you can specify which validations are required
                before the agent can use it.
              </p>

              {/* Tool Group Navigation */}
              <div className="d-flex flex-wrap mb-0">
                {Object.entries(TOOL_GROUPS).map(([groupKey, groupValue]) => (
                  <Button
                    key={groupKey}
                    variant={
                      expandedSection === `tool-group-${groupKey}`
                        ? 'secondary'
                        : 'phoenix-secondary'
                    }
                    className={
                      expandedSection === `tool-group-${groupKey}`
                        ? 'bg-body-quaternary me-2 mb-2'
                        : 'me-2 mb-2'
                    }
                    size="sm"
                    onClick={() => handleToolGroupChange(groupKey)}
                  >
                    {groupValue}
                    {groupedAvailableTools[groupValue]?.length > 0 && (
                      <span className="ms-1 badge bg-secondary-light text-body">
                        {groupedAvailableTools[groupValue].length}
                      </span>
                    )}
                  </Button>
                ))}
              </div>

              {/* Tools for the selected group */}
              {Object.entries(TOOL_GROUPS).map(([groupKey, groupValue]) => (
                <div
                  key={`tool-section-${groupKey}`}
                  className={
                    expandedSection === `tool-group-${groupKey}`
                      ? 'd-block'
                      : 'd-none'
                  }
                >
                  <div
                    className="scrollbar bg-body rounded border px-3 pt-2"
                    style={{ height: '450px', overflowY: 'auto' }}
                  >
                    {/* Header de columnas */}
                    <div
                      className={`row pb-1 pt-1 mb-2 ${
                        isDark ? 'bg-secondary' : 'bg-secondary-lighter'
                      } rounded-3 mx-1 mt-1 fs-9`}
                    >
                      <div className="col-4">
                        <strong>Tool</strong>
                      </div>
                      <div className="col-4 text-center border-start border-end">
                        <strong>For Outbound Calls</strong>
                      </div>
                      <div className="col-4 text-center">
                        <strong>For Inbound Calls</strong>
                      </div>
                    </div>

                    {/* Herramientas */}
                    {groupedAvailableTools[groupValue]?.length > 0 ? (
                      groupedAvailableTools[groupValue].map(tool => (
                        <div
                          key={tool.toolid}
                          className="row mb-2 pb-0 pt-1 mx-1 fs-9 rounded-3 bg-body-emphasis"
                        >
                          {/* Columna 1: Nombre de la herramienta */}
                          <div className="col-4">
                            <div className="d-flex align-items-start">
                              <span className="me-2 fw-bold">{tool.name}</span>
                              <OverlayTrigger
                                placement="right"
                                overlay={
                                  <Tooltip id={`tooltip-${tool.toolid}`}>
                                    {tool.description}
                                  </Tooltip>
                                }
                              >
                                <FontAwesomeIcon
                                  icon={faInfoCircle}
                                  className="text-secondary-light fs-9 pt-1"
                                />
                              </OverlayTrigger>
                            </div>
                          </div>

                          {/* Columna 2: Outbound */}
                          <div className="col-4">
                            <Form.Check
                              type="switch"
                              id={`tool-outbound-${tool.toolid}`}
                              label={
                                <span className="fs-9 fw-normal">
                                  {isToolAssigned(tool.toolid, 'Outbound')
                                    ? 'Assigned to the Agent'
                                    : 'Not assigned to the Agent'}
                                </span>
                              }
                              checked={isToolAssigned(tool.toolid, 'Outbound')}
                              onChange={e =>
                                toggleToolSelection(
                                  tool.toolid,
                                  e.target.checked,
                                  'Outbound'
                                )
                              }
                              className="mb-0"
                            />

                            {/* Validaciones para Outbound */}
                            {isToolAssigned(tool.toolid, 'Outbound') &&
                              availableValidations.length > 0 &&
                              groupKey !== 'REQUEST_VALIDATION' && (
                                <div className="pt-1 mb-2 px-4 rounded-3 bg-body">
                                  <div className="fs-10 fw-bold text-primary mb-1">
                                    Select the validations needed before using
                                    the tool:
                                  </div>
                                  {availableValidations.map(validation => (
                                    <Form.Check
                                      key={`outbound-${validation.validationid}`}
                                      type="checkbox"
                                      id={`validation-outbound-${tool.toolid}-${validation.validationid}`}
                                      label={validation.name}
                                      checked={getToolValidations(
                                        tool.toolid,
                                        'Outbound'
                                      ).includes(validation.validationid)}
                                      onChange={e =>
                                        toggleValidationForTool(
                                          tool.toolid,
                                          validation.validationid,
                                          e.target.checked,
                                          'Outbound'
                                        )
                                      }
                                      className="mb-0 fs-9"
                                    />
                                  ))}
                                </div>
                              )}
                          </div>

                          {/* Columna 3: Inbound */}
                          <div className="col-4">
                            <Form.Check
                              type="switch"
                              id={`tool-inbound-${tool.toolid}`}
                              label={
                                <span className="fs-9 fw-normal">
                                  {isToolAssigned(tool.toolid, 'Inbound')
                                    ? 'Assigned to the Agent'
                                    : 'Not assigned to the Agent'}
                                </span>
                              }
                              checked={isToolAssigned(tool.toolid, 'Inbound')}
                              onChange={e =>
                                toggleToolSelection(
                                  tool.toolid,
                                  e.target.checked,
                                  'Inbound'
                                )
                              }
                              className="mb-0"
                            />

                            {/* Validaciones para Inbound */}
                            {isToolAssigned(tool.toolid, 'Inbound') &&
                              availableValidations.length > 0 &&
                              groupKey !== 'REQUEST_VALIDATION' && (
                                <div className="pt-1 mb-2 px-4 rounded-3 bg-body">
                                  <div className="fs-10 fw-bold text-primary mb-1">
                                    Select the validations needed before using
                                    the tool:
                                  </div>
                                  {availableValidations.map(validation => (
                                    <Form.Check
                                      key={`inbound-${validation.validationid}`}
                                      type="checkbox"
                                      id={`validation-inbound-${tool.toolid}-${validation.validationid}`}
                                      label={validation.name}
                                      checked={getToolValidations(
                                        tool.toolid,
                                        'Inbound'
                                      ).includes(validation.validationid)}
                                      onChange={e =>
                                        toggleValidationForTool(
                                          tool.toolid,
                                          validation.validationid,
                                          e.target.checked,
                                          'Inbound'
                                        )
                                      }
                                      className="fs-9 mb-0"
                                    />
                                  ))}
                                </div>
                              )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted fs-9">
                        No tools available in this category
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Diagrama Mermaid */}
      <MermaidDiagramModal
        show={showDiagramModal}
        onHide={() => setShowDiagramModal(false)}
        agentId={agentId}
        agentName="Agent" // Podrías pasar el nombre real del agente si lo tienes
        promptType={diagramPromptType}
        isDarkMode={isDark}
      />
    </div>
  );
};

export default AgentConfigColumn;
